/**
 * LineLeaderHome (QGS v2)
 *
 * Dedicated landing for user_type === 'LINE_LEADER'. Replaces the
 * operator dashboard entirely for LL sessions: no draft/completed/KO/
 * rework stat cards, no tabs — just four big tiles plus the sub-flows.
 *
 * Tiles:
 *   1. Approval Queue   — delegates up to App.js (reuses existing
 *                         ApprovalQueueScreen step)
 *   2. View Open KOs    — all KOs with status=OPEN (sub-view here)
 *   3. View Open Reworks— all Rework records (sub-view here)
 *   4. Scan QR Code     — scan a unit serial OR pick from a fallback
 *                         list of serials with open KOs → unit detail
 *                         where the LL approves / rejects closures and
 *                         reworks (each gated by PIN).
 */
import { useCallback, useEffect, useState } from 'react';
import LlPinPromptModal from './LlPinPromptModal';

const API_BASE = process.env.REACT_APP_BASE_API_URL
  || process.env.REACT_APP_API_URL
  || 'http://localhost:8000';
const API = API_BASE + '/api/frontend/qgs';

async function fetchCsrfToken() {
  try {
    const r = await fetch(`${API_BASE}/api/frontend/auth/csrf-token/`, { credentials: 'include' });
    if (r.ok) {
      const b = await r.json();
      return b?.data?.csrfToken || '';
    }
  } catch { /* offline */ }
  return '';
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
}


export default function LineLeaderHome({ user, onOpenApprovalQueue, onSignOut }) {
  const [view, setView] = useState('home');
  // 'home' | 'openKos' | 'openReworks' | 'scan' | 'serialDetail'
  const [selectedSerial, setSelectedSerial] = useState('');

  const header = (
    <header style={{
      position: 'sticky', top: 0, zIndex: 10, background: '#fff',
      borderBottom: '1px solid #e5eaf0', padding: '14px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/daikin-logo.png" alt="Daikin" style={{ height: 28 }} />
        <div style={{
          padding: '3px 8px', background: '#4f46e5', color: '#fff',
          borderRadius: 6, fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
        }}>QGS · LINE LEADER</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {view !== 'home' && (
          <button onClick={() => setView('home')} style={navBtn}>← Home</button>
        )}
        <span style={{ fontSize: 12, color: '#4a596b' }}>
          {user?.username || 'LL'}
        </span>
        {onSignOut && <button onClick={onSignOut} style={navBtn}>Sign out</button>}
      </div>
    </header>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f7f8fa' }}>
      {header}
      {view === 'home' && (
        <HomeTiles
          onApprovalQueue={onOpenApprovalQueue}
          onOpenKos={() => setView('openKos')}
          onOpenReworks={() => setView('openReworks')}
          onScan={() => setView('scan')}
        />
      )}
      {view === 'openKos' && (
        <OpenKosList
          onPickSerial={(serial) => { setSelectedSerial(serial); setView('serialDetail'); }}
        />
      )}
      {view === 'openReworks' && <OpenReworksList />}
      {view === 'scan' && (
        <ScanSerial
          onPicked={(serial) => { setSelectedSerial(serial); setView('serialDetail'); }}
        />
      )}
      {view === 'serialDetail' && selectedSerial && (
        <SerialDetail
          serial={selectedSerial}
          onBack={() => setView('scan')}
        />
      )}
    </div>
  );
}


// -----------------------------------------------------------------------
// Home — four tiles
// -----------------------------------------------------------------------
function HomeTiles({ onApprovalQueue, onOpenKos, onOpenReworks, onScan }) {
  const [counts, setCounts] = useState({ approvals: null, openKos: null, openReworks: null, serials: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [aRes, kRes, rRes] = await Promise.all([
          fetch(`${API}/reports/approvals/`, { credentials: 'include' }),
          fetch(`${API}/reports/knockouts/?status=OPEN&page_size=500`, { credentials: 'include' }),
          fetch(`${API}/rework-records/?page_size=500`, { credentials: 'include' }),
        ]);
        const a = aRes.ok ? (await aRes.json()).data || [] : [];
        const kBody = kRes.ok ? await kRes.json() : {};
        const kos = kBody.data || [];
        const rBody = rRes.ok ? await rRes.json() : {};
        const rwRaw = rBody.results || rBody.data || rBody;
        const rws = Array.isArray(rwRaw) ? rwRaw : [];
        const serials = new Set(kos.map(k => k.unit_serial).filter(Boolean));
        if (!cancelled) {
          setCounts({
            approvals: a.length,
            openKos: kos.length,
            openReworks: rws.length,
            serials: serials.size,
          });
        }
      } catch {
        // silent — tile just shows no badge
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const badge = (n, bg, fg) => (n === null || n === undefined) ? null : (
    <span style={{
      position: 'absolute', top: 10, right: 12,
      minWidth: 24, height: 24, padding: '0 7px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 999, background: bg, color: fg,
      fontSize: 12, fontWeight: 800,
    }}>{n}</span>
  );

  const tile = (bg, border, color, icon, title, subtitle, onClick, badgeNode) => (
    <button
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center',
        padding: 24, border: `2px solid ${border}`, background: bg,
        borderRadius: 14, cursor: 'pointer', minHeight: 160,
        transition: 'transform .12s, box-shadow .12s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,34,55,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {badgeNode}
      <div style={{ fontSize: 42, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color }}>{title}</div>
      <div style={{ fontSize: 12, color: '#4a596b', marginTop: 6, maxWidth: 220 }}>{subtitle}</div>
    </button>
  );
  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ margin: '12px 0 6px', fontSize: 22, color: '#17253d' }}>
        Line Leader workspace
      </h1>
      <p style={{ margin: 0, color: '#4a596b', fontSize: 13 }}>
        Approve pending signatures, inspect the open backlog, or scan a unit to review its KOs and reworks.
      </p>
      <div style={{
        marginTop: 20,
        display: 'grid', gap: 16,
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
      }}>
        {onApprovalQueue && tile('#eef2ff', '#c7d2fe', '#3730a3', '✅', 'Approval Queue',
          'Sign off pending KOs, closures and reworks', onApprovalQueue,
          badge(counts.approvals, '#4f46e5', '#fff'))}
        {tile('#fef2f2', '#fecaca', '#991b1b', '🚫', 'View Open KOs',
          'All knockouts currently open across the floor', onOpenKos,
          badge(counts.openKos, '#dc2626', '#fff'))}
        {tile('#fff7ed', '#fed7aa', '#9a3412', '🔧', 'View Open Reworks',
          'All rework records awaiting or in progress', onOpenReworks,
          badge(counts.openReworks, '#ea580c', '#fff'))}
        {tile('#f0fdf4', '#bbf7d0', '#166534', '📷', 'Scan QR Code',
          'Scan a unit serial to see its open KOs and reworks', onScan,
          badge(counts.serials, '#16a34a', '#fff'))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Open KOs — flat list across the floor
// -----------------------------------------------------------------------
function OpenKosList({ onPickSerial }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/reports/knockouts/?status=OPEN&page_size=200`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.json();
      setRows(b.data || []);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto' }}>
      <SectionHeader title="Open KOs" count={rows.length} onRefresh={load} loading={loading} />
      {err && <ErrorBox text={err} />}
      {!loading && !err && rows.length === 0 && <EmptyBox text="No open KOs — nothing to do." tone="good" />}
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map(k => (
          <button
            key={k.id}
            onClick={() => onPickSerial(k.unit_serial)}
            style={rowCard}
          >
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b' }}>
                {k.ko_id || `#${k.id}`} · <span style={pill('#fee2e2', '#991b1b')}>OPEN</span>
                {k.closure_pending && <span style={{ marginLeft: 6, ...pill('#fef3c7', '#92400e') }}>CLOSURE PENDING</span>}
                {k.gate && <span style={{ marginLeft: 6, color: '#8592a3', fontSize: 11 }}>· {k.gate}</span>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                {k.defect_type || '—'}
                {k.defect_location ? ` · ${k.defect_location}` : ''}
              </div>
              <div style={{ fontSize: 12, color: '#4a596b', marginTop: 2 }}>
                Unit <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{k.unit_serial}</span>
                {k.creator ? ` · by ${k.creator}` : ''}
                {k.created_at ? ` · ${fmtWhen(k.created_at)}` : ''}
              </div>
            </div>
            <div style={{ color: '#4f46e5', fontSize: 12, fontWeight: 700 }}>Review ›</div>
          </button>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Open Reworks
// -----------------------------------------------------------------------
function OpenReworksList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/rework-records/?page_size=200`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.json();
      const data = b.results || b.data || b;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto' }}>
      <SectionHeader title="Open Reworks" count={rows.length} onRefresh={load} loading={loading} />
      {err && <ErrorBox text={err} />}
      {!loading && !err && rows.length === 0 && <EmptyBox text="No reworks on record." tone="good" />}
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map(r => (
          <div key={r.id} style={{ ...rowCard, cursor: 'default' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b' }}>
                REWORK-{r.id} · <span style={pill(reworkStatusColor(r.approval_status).bg, reworkStatusColor(r.approval_status).fg)}>
                  {r.approval_status || 'PENDING'}
                </span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                {r.rework_type_label || r.rework_type?.label_en || '—'}
              </div>
              <div style={{ fontSize: 12, color: '#4a596b', marginTop: 2 }}>
                Submission #{r.submission}
                {r.created_at ? ` · ${fmtWhen(r.created_at)}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Scan serial — manual entry + fallback list of serials with open KOs
// -----------------------------------------------------------------------
function ScanSerial({ onPicked }) {
  const [manual, setManual] = useState('');
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reports/knockouts/?status=OPEN&page_size=500`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const b = await r.json();
        const map = new Map();
        (b.data || []).forEach(k => {
          const key = k.unit_serial;
          if (!key) return;
          const row = map.get(key) || { serial: key, open_kos: 0, closure_pending: 0 };
          row.open_kos += 1;
          if (k.closure_pending) row.closure_pending += 1;
          map.set(key, row);
        });
        setSerials(Array.from(map.values()).sort((a, b) => a.serial.localeCompare(b.serial)));
      } catch { /* list is a convenience; silent fail */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div style={{ padding: 20, maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, margin: '8px 0 4px' }}>Scan unit QR</h2>
      <p style={{ margin: 0, color: '#4a596b', fontSize: 13 }}>
        Scan the unit's serial, or enter it below. Then pick it to see its KOs & reworks.
      </p>

      <div style={{
        background: '#fff', border: '2px dashed #cfd6de', borderRadius: 12,
        padding: 30, textAlign: 'center', margin: '18px 0',
      }}>
        <div style={{ fontSize: 44, marginBottom: 6 }}>📷</div>
        <div style={{ color: '#4a596b', fontSize: 13 }}>Camera scan comes from the device</div>
        <div style={{ color: '#8592a3', fontSize: 11, marginTop: 4 }}>
          (On tablets without camera access, use manual entry below)
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5eaf0', borderRadius: 10, padding: 14, marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4a596b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          Enter serial manually
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={manual}
            onChange={e => setManual(e.target.value.toUpperCase())}
            placeholder="CH-26G24262-KKKKXX"
            style={{
              flex: 1, padding: 10, border: '1px solid #cfd6de', borderRadius: 6,
              fontSize: 14, fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}
          />
          <button
            disabled={manual.length < 3}
            onClick={() => onPicked(manual.trim())}
            style={{
              padding: '10px 18px', border: 'none', borderRadius: 6,
              background: manual.length >= 3 ? '#2b7fd0' : '#8592a3',
              color: '#fff', fontWeight: 700,
              cursor: manual.length >= 3 ? 'pointer' : 'not-allowed',
            }}
          >Continue</button>
        </div>
      </div>

      <h3 style={{ fontSize: 14, color: '#4a596b', margin: '14px 0 8px' }}>
        {loading ? 'Loading serials with open KOs…' : `Serials with open KOs (${serials.length})`}
      </h3>
      {!loading && serials.length === 0 && (
        <EmptyBox text="Nothing open on any serial." tone="good" />
      )}
      <div style={{ display: 'grid', gap: 8 }}>
        {serials.map(s => (
          <button key={s.serial} onClick={() => onPicked(s.serial)} style={rowCard}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 14, fontWeight: 700 }}>
                {s.serial}
              </div>
              <div style={{ fontSize: 12, color: '#4a596b', marginTop: 2 }}>
                {s.open_kos} open KO{s.open_kos === 1 ? '' : 's'}
                {s.closure_pending > 0 && ` · ${s.closure_pending} awaiting your closure signature`}
              </div>
            </div>
            <div style={{ color: '#4f46e5', fontSize: 12, fontWeight: 700 }}>Open ›</div>
          </button>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Serial detail — open KOs + reworks for one serial, with PIN approve/reject
// -----------------------------------------------------------------------
function SerialDetail({ serial, onBack }) {
  const [kos, setKos] = useState([]);
  const [reworks, setReworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [pinPrompt, setPinPrompt] = useState({ open: false, decision: null, approvalId: null, label: '' });

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [koRes, rwRes] = await Promise.all([
        fetch(`${API}/reports/knockouts/?unit_serial=${encodeURIComponent(serial)}&status=OPEN&page_size=200`,
              { credentials: 'include' }),
        fetch(`${API}/rework-records/?unit_serial=${encodeURIComponent(serial)}&page_size=200`,
              { credentials: 'include' }),
      ]);
      if (!koRes.ok) throw new Error(`KOs HTTP ${koRes.status}`);
      if (!rwRes.ok) throw new Error(`Reworks HTTP ${rwRes.status}`);
      const koBody = await koRes.json();
      const rwBody = await rwRes.json();
      setKos(koBody.data || []);
      const rwData = rwBody.results || rwBody.data || rwBody;
      setReworks(Array.isArray(rwData) ? rwData : []);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [serial]);

  useEffect(() => { load(); }, [load]);

  const openPinPrompt = (decision, approvalId, label) =>
    setPinPrompt({ open: true, decision, approvalId, label });

  const submitPinDecision = async ({ username, pin, reason }) => {
    const csrf = await fetchCsrfToken();
    const url = `${API}/reports/approvals/${pinPrompt.approvalId}/${pinPrompt.decision}/`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRFToken': csrf } : {}),
      },
      body: JSON.stringify({
        approver_username: username,
        pin,
        ...(reason ? { reason } : {}),
      }),
    });
    if (!res.ok) {
      let body = {};
      try { body = await res.json(); } catch { /* ignore */ }
      const e = new Error(body.error || `HTTP ${res.status}`);
      e.status = res.status;
      e.body = body;
      throw e;
    }
    setPinPrompt({ open: false, decision: null, approvalId: null, label: '' });
    await load();
  };

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#17253d' }}>
            Unit <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{serial}</span>
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#4a596b' }}>
            Review open KOs and reworks for this unit. Each decision needs your PIN.
          </p>
        </div>
        <button onClick={onBack} style={navBtn}>← Scan another</button>
      </div>

      {err && <ErrorBox text={err} />}
      {loading && <div style={{ padding: 20, color: '#8592a3' }}>Loading…</div>}

      {!loading && !err && (
        <>
          <h3 style={sectionTitle}>Open KOs ({kos.length})</h3>
          {kos.length === 0 && <EmptyBox text={`No open KOs on ${serial}.`} tone="good" />}
          <div style={{ display: 'grid', gap: 10 }}>
            {kos.map(k => {
              const canApprove = k.closure_pending === true; // Only closure-pending can be approved
              const approvalId = `CLOSURE-${k.id}`;
              return (
                <div key={k.id} style={koCard}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b' }}>
                      {k.ko_id || `#${k.id}`} · <span style={pill('#fee2e2', '#991b1b')}>OPEN</span>
                      {k.closure_pending && <span style={{ marginLeft: 6, ...pill('#fef3c7', '#92400e') }}>CLOSURE PENDING</span>}
                      {k.gate && <span style={{ marginLeft: 6, color: '#8592a3', fontSize: 11 }}>· {k.gate}</span>}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                      {k.defect_type || '—'}{k.defect_location ? ` · ${k.defect_location}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#4a596b', marginTop: 2 }}>
                      {k.creator ? `Raised by ${k.creator}` : ''}
                      {k.created_at ? ` · ${fmtWhen(k.created_at)}` : ''}
                      {k.rework_type ? ` · Rework: ${k.rework_type}` : ''}
                    </div>
                    {!canApprove && (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#92400e' }}>
                        Waiting for the operator to mark rework complete before this KO can be closed.
                      </div>
                    )}
                  </div>
                  {canApprove && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 110 }}>
                      <button
                        style={btnApprove}
                        onClick={() => openPinPrompt('approve', approvalId, `Close ${k.ko_id || `KO#${k.id}`}`)}
                      >Approve closure</button>
                      <button
                        style={btnReject}
                        onClick={() => openPinPrompt('reject', approvalId, `Reject closure for ${k.ko_id || `KO#${k.id}`}`)}
                      >Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <h3 style={sectionTitle}>Reworks on this unit ({reworks.length})</h3>
          {reworks.length === 0 && <EmptyBox text="No rework records for this unit." tone="neutral" />}
          <div style={{ display: 'grid', gap: 10 }}>
            {reworks.map(r => {
              const canDecide = (r.approval_status || 'PENDING_APPROVAL') === 'PENDING_APPROVAL';
              const approvalId = `REWORK-${r.id}`;
              const col = reworkStatusColor(r.approval_status);
              return (
                <div key={r.id} style={koCard}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: '#4a596b' }}>
                      REWORK-{r.id} · <span style={pill(col.bg, col.fg)}>{r.approval_status || 'PENDING'}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                      {r.rework_type_label || r.rework_type?.label_en || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: '#4a596b', marginTop: 2 }}>
                      {r.created_at ? fmtWhen(r.created_at) : ''}
                      {r.rejection_reason ? ` · ${r.rejection_reason}` : ''}
                    </div>
                  </div>
                  {canDecide && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 110 }}>
                      <button style={btnApprove} onClick={() => openPinPrompt('approve', approvalId, `Approve rework #${r.id}`)}>
                        Approve
                      </button>
                      <button style={btnReject} onClick={() => openPinPrompt('reject', approvalId, `Reject rework #${r.id}`)}>
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <LlPinPromptModal
        isOpen={pinPrompt.open}
        onClose={() => setPinPrompt({ open: false, decision: null, approvalId: null, label: '' })}
        onSubmit={submitPinDecision}
        title={pinPrompt.decision === 'reject' ? 'Reject — Line Leader signature' : 'Approve — Line Leader signature'}
        subtitle={pinPrompt.label}
        ctaLabel={pinPrompt.decision === 'reject' ? 'Reject' : 'Approve'}
        ctaColor={pinPrompt.decision === 'reject' ? '#dc2626' : '#22a06b'}
        requireReason={pinPrompt.decision === 'reject'}
      />
    </div>
  );
}


// ---------- shared tiny bits ----------
const navBtn = {
  padding: '7px 12px', background: '#fff', border: '1px solid #cfd6de',
  borderRadius: 6, fontSize: 12, color: '#4a596b', cursor: 'pointer',
};
const rowCard = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, background: '#fff', border: '1px solid #e5eaf0', borderRadius: 10,
  padding: '12px 14px', cursor: 'pointer', textAlign: 'left', width: '100%',
};
const koCard = {
  display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
  background: '#fff', border: '1px solid #e5eaf0', borderRadius: 10, padding: 14,
};
const sectionTitle = { marginTop: 20, marginBottom: 8, fontSize: 14, color: '#17253d' };
const btnApprove = {
  padding: '8px 10px', border: 'none', borderRadius: 6,
  background: '#22a06b', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const btnReject = {
  padding: '8px 10px', border: '1px solid #fca5a5', borderRadius: 6,
  background: '#fff', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const pill = (bg, fg) => ({
  display: 'inline-block', padding: '2px 7px', borderRadius: 999,
  background: bg, color: fg, fontSize: 10, fontWeight: 700,
});
const reworkStatusColor = (s) => {
  if (s === 'APPROVED') return { bg: '#dcfce7', fg: '#166534' };
  if (s === 'REJECTED') return { bg: '#fee2e2', fg: '#991b1b' };
  return { bg: '#fef3c7', fg: '#92400e' };
};

function SectionHeader({ title, count, onRefresh, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h2 style={{ margin: 0, fontSize: 18, color: '#17253d' }}>{title} ({count})</h2>
      <button onClick={onRefresh} disabled={loading} style={navBtn}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  );
}
function ErrorBox({ text }) {
  return (
    <div style={{
      padding: 12, borderRadius: 8, background: '#fce8e6',
      border: '1px solid #f5b7b1', color: '#8a2c22', fontSize: 13, marginBottom: 10,
    }}>{text}</div>
  );
}
function EmptyBox({ text, tone }) {
  const styles = tone === 'good'
    ? { bg: '#e6f6ee', fg: '#0f5a3a' }
    : { bg: '#f1f5f9', fg: '#334155' };
  return (
    <div style={{
      padding: 20, borderRadius: 8, background: styles.bg, color: styles.fg,
      fontSize: 13, textAlign: 'center', marginBottom: 10,
    }}>{text}</div>
  );
}
